import { FC, useRef, useCallback, useEffect, useState } from "react";
import { Stack } from "@mui/material";
import {
  Editor,
  EditorState,
  AtomicBlockUtils,
  ContentBlock,
  Modifier,
  SelectionState,
  getDefaultKeyBinding,
} from "draft-js";
import { useGetUniversalSearchQuery } from "@/store/api/userApi";
import { insertMentionToEditor } from "./mentions";
import TwitterEmbed from "@/components/TwitterEmbed";
import EmbeddedPost from "@/components/EmbeddedPost";
import { useGetOembedQuery } from "@/store/api/oembedApi";
import { skipToken } from "@reduxjs/toolkit/query";
import ExternalPreview from "@/components/ExternalPreview";
import { TICKER_TRIGGER_REGEX } from "./tickerSchema";
import MentionDropdown from "./MentionDropdown";
import { AtomicYouTube } from "@/components/YouTubeEmbed";

interface PostEditorProps {
  editorState: EditorState;
  setEditorState: (state: EditorState) => void;
  placeholder?: string;
  maxChars: number;

  setSearchTerm: (v: string) => void;
  setDropdownItems: (arr: any[]) => void;
  setHighlightedIndex: (i: number) => void;
  // setDropdownPos: (pos: { top: number; left: number }) => void;
  onSelectMention: (item: any) => void;
  searchTerm: string;
  highlightedIndex: number;
  dropdownItems: any[];
  postVariant: string;
}

const PostEditor: FC<PostEditorProps> = ({
  editorState,
  setEditorState,
  placeholder,
  maxChars,
  setSearchTerm,
  setDropdownItems,
  setHighlightedIndex,

  searchTerm,
  highlightedIndex,
  dropdownItems,
  postVariant,
}) => {
  const editorRef = useRef<Editor>(null);
  const dropdownAnchorRef = useRef<{
    blockKey: string;
    startOffset: number;
    pos: { top: number; left: number };
  } | null>(null);
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const { data: searchCompanies, isFetching } = useGetUniversalSearchQuery(
    { limit: 5, query: searchTerm, type: "instruments" },
    { skip: !searchTerm }
  );

  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const items = (searchCompanies?.results ?? []).map((c: any) => ({
    data: {
      name: c.data.name,
      code: c.data.code,
      type: c.data.type,
    },
  }));
  useEffect(() => {
    if (isFetching) {
      setDropdownItems([]);
      return;
    }
    setDropdownItems(items);
  }, [items, isFetching, setDropdownItems]);
  useEffect(() => {
    setDropdownItems(items);
  }, [items, setDropdownItems]);

  const extractInternalPostUrls = useCallback((text: string): string[] => {
    const regex =
      /https?:\/\/(?:wethemrkt\.com|wethermarket-develop-[^\/]+\.ondigitalocean\.app)\/post\/([a-zA-Z0-9-]+)/gi;
    const matches = Array.from(text.matchAll(regex));
    return matches.map((m) => m[0]);
  }, []);

  const extractTwitterUrls = useCallback((text: string): string[] => {
    const regex = /https?:\/\/(?:twitter\.com|x\.com)\/[^\s\/]+\/status\/\d+/gi;
    return Array.from(new Set(text.match(regex) || []));
  }, []);

  const extractExternalUrls = useCallback((text: string): string[] => {
    const regex = /https?:\/\/[^\s]+/gi;
    const allUrls = Array.from(new Set(text.match(regex) || []));

    return allUrls.filter(
      (url) =>
        !/https?:\/\/(?:wethemrkt\.com|wethermarket-develop-[^\/]+\.ondigitalocean\.app)\/post\//i.test(
          url
        ) && !/https?:\/\/(?:twitter\.com|x\.com)\//i.test(url)
    );
  }, []);

  const extractYouTubeUrls = useCallback((text: string): string[] => {
    const regex = /https?:\/\/(?:www\.)?youtube\.com\/watch\?v=[\w-]+/gi;
    const shortRegex = /https?:\/\/youtu\.be\/[\w-]+/gi;
    const urls = Array.from(new Set(text.match(regex) || []));
    const shortUrls = Array.from(new Set(text.match(shortRegex) || []));
    return [...urls, ...shortUrls];
  }, []);

  const insertEmbeddedPost = useCallback((state: EditorState, url: string): EditorState => {
    const contentState = state.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity("POST", "IMMUTABLE", { url });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newState = AtomicBlockUtils.insertAtomicBlock(state, entityKey, " ");

    return EditorState.forceSelection(newState, newState.getCurrentContent().getSelectionAfter());
  }, []);

  const extractPostUid = (url: string) => {
    const match = url.match(TICKER_TRIGGER_REGEX);
    return match ? match[1] : null;
  };

  const insertTweet = useCallback((state: EditorState, url: string): EditorState => {
    const contentState = state.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity("TWITTER", "IMMUTABLE", { url });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newState = AtomicBlockUtils.insertAtomicBlock(state, entityKey, " ");

    return EditorState.forceSelection(newState, newState.getCurrentContent().getSelectionAfter());
  }, []);

  const insertExternalPreview = useCallback((state: EditorState, url: string): EditorState => {
    const contentState = state.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity("EXTERNAL", "IMMUTABLE", { url });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newState = AtomicBlockUtils.insertAtomicBlock(state, entityKey, " ");
    return EditorState.forceSelection(newState, newState.getCurrentContent().getSelectionAfter());
  }, []);

  const insertYouTubePreview = useCallback((state: EditorState, url: string): EditorState => {
    const contentState = state.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity("YOUTUBE", "IMMUTABLE", { url });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newState = AtomicBlockUtils.insertAtomicBlock(state, entityKey, " ");
    return EditorState.forceSelection(newState, newState.getCurrentContent().getSelectionAfter());
  }, []);

  const extractTweetId = (url: string) => {
    const match = url.match(/status\/(\d+)/);
    return match ? match[1] : null;
  };

  const syncEmbeddedBlocks = useCallback(
    (state: EditorState): EditorState => {
      const text = state.getCurrentContent().getPlainText();

      const orderedMatches: { url: string; type: "POST" | "TWITTER" | "EXTERNAL" | "YOUTUBE" }[] =
        [];

      const regex = /https?:\/\/[^\s]+/gi;
      let match;
      while ((match = regex.exec(text)) !== null) {
        const url = match[0];
        if (extractInternalPostUrls(url).length > 0) orderedMatches.push({ url, type: "POST" });
        else if (extractTwitterUrls(url).length > 0) orderedMatches.push({ url, type: "TWITTER" });
        else if (extractYouTubeUrls(url).length > 0) orderedMatches.push({ url, type: "YOUTUBE" });
        else if (extractExternalUrls(url).length > 0)
          orderedMatches.push({ url, type: "EXTERNAL" });
      }

      const firstUrl = orderedMatches[0]?.url || null;
      const firstType = orderedMatches[0]?.type || null;

      let content = state.getCurrentContent();
      const blocks = content.getBlockMap();
      let hasChanges = false;

      blocks.forEach((block) => {
        if (!block || block.getType() !== "atomic") return;
        const key = block.getEntityAt(0);
        if (!key) return;

        const entity = content.getEntity(key);
        const url = entity.getData().url;

        if (url !== firstUrl) {
          const blockSelection = SelectionState.createEmpty(block.getKey()).merge({
            anchorOffset: 0,
            focusOffset: block.getLength(),
          });
          content = Modifier.removeRange(content, blockSelection, "backward");
          hasChanges = true;
        }
      });

      let newState = hasChanges ? EditorState.push(state, content, "remove-range") : state;

      if (!firstUrl) return newState;

      const exists = newState
        .getCurrentContent()
        .getBlockMap()
        .some((block) => {
          if (!block || block.getType() !== "atomic") return false;
          const key = block.getEntityAt(0);
          if (!key) return false;
          const entity = newState.getCurrentContent().getEntity(key);
          return entity.getData().url === firstUrl;
        });

      if (!exists && firstType) {
        if (firstType === "POST") {
          newState = insertEmbeddedPost(newState, firstUrl);
        } else if (firstType === "TWITTER") {
          newState = insertTweet(newState, firstUrl);
        } else if (firstType === "YOUTUBE") {
          newState = insertYouTubePreview(newState, firstUrl);
        } else if (firstType === "EXTERNAL") {
          newState = insertExternalPreview(newState, firstUrl);
        }
      }

      return newState;
    },
    [
      extractInternalPostUrls,
      extractTwitterUrls,
      extractExternalUrls,
      insertEmbeddedPost,
      insertTweet,
      insertExternalPreview,
    ]
  );

  const onChange = useCallback(
    (newState: EditorState) => {
      const selection = newState.getSelection();
      const block = newState.getCurrentContent().getBlockForKey(selection.getStartKey());

      if (block) {
        const blockText = block.getText();
        const cursorPos = selection.getStartOffset();
        const textBefore = blockText.slice(0, cursorPos);

        const match = textBefore.match(TICKER_TRIGGER_REGEX);
        if (match) {
          const term = match[1];
          const triggerStart = match.index!;
          const blockKey = selection.getStartKey();

          setSearchTerm(term);
          setHighlightedIndex(0);

          const sameAnchor =
            dropdownAnchorRef.current &&
            dropdownAnchorRef.current.blockKey === blockKey &&
            dropdownAnchorRef.current.startOffset === triggerStart;

          if (!sameAnchor) {
            requestAnimationFrame(() => {
              const sel = window.getSelection();
              if (!sel || sel.rangeCount === 0) return;

              const range = sel.getRangeAt(0);
              const rect = range.getBoundingClientRect();
              if (rect.width === 0 && rect.height === 0) return;

              const wrapperBounds = editorWrapperRef.current?.getBoundingClientRect();
              if (!wrapperBounds) return;

              const pos = {
                top: rect.bottom - wrapperBounds.top,
                left: rect.left - wrapperBounds.left,
              };

              dropdownAnchorRef.current = {
                blockKey,
                startOffset: triggerStart,
                pos,
              };

              setDropdownPos(pos);
            });
          }
        } else {
          setSearchTerm("");
          dropdownAnchorRef.current = null;
        }
      }

      const stateWithSyncedBlocks = syncEmbeddedBlocks(newState);
      setEditorState(stateWithSyncedBlocks);
    },
    [syncEmbeddedBlocks, setEditorState]
  );

  const insertMention = useCallback(
    (mention: { data: { code: string; name?: string } }) => {
      const newState = insertMentionToEditor(editorState, mention.data.code, mention.data.name);
      setEditorState(newState);
      setSearchTerm("");

      setTimeout(() => editorRef.current?.focus(), 0);
    },
    [editorState, setEditorState]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!searchTerm || dropdownItems.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((highlightedIndex + 1) % dropdownItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(highlightedIndex === 0 ? dropdownItems.length - 1 : highlightedIndex - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      insertMention(dropdownItems[highlightedIndex]);
    } else if (e.key === "Escape") {
      setSearchTerm("");
    }
  };

  const handleBeforeInput = useCallback(
    (chars: string, state: EditorState) => {
      const currentContent = state.getCurrentContent();
      const plainText = currentContent.getPlainText();

      if (plainText.length + chars.length > maxChars) {
        return "handled";
      }

      return "not-handled";
    },
    [maxChars]
  );

  const handlePastedText = useCallback(
    (text: string, _html: string | undefined, state: EditorState) => {
      const content = state.getCurrentContent();
      const selection = state.getSelection();

      const plainTextLength = content.getPlainText("").length;

      if (plainTextLength + text.length > maxChars) {
        return "handled";
      }

      const newContent = Modifier.replaceText(content, selection, text, undefined);

      const newEditorState = EditorState.push(state, newContent, "insert-characters");

      onChange(newEditorState);

      return "handled";
    },
    [maxChars, onChange]
  );
  const AtomicPost: FC<{ block: ContentBlock }> = ({ block }) => {
    const contentState = editorState.getCurrentContent();
    const key = block.getEntityAt(0);
    if (!key) return null;
    const entity = contentState.getEntity(key);
    const url = entity.getData().url;
    const uid = extractPostUid(url);
    if (!uid) return null;

    return (
      <div style={{ margin: "0.5rem 0" }}>
        <EmbeddedPost uid={uid} />
      </div>
    );
  };

  const AtomicTweet: FC<{ block: ContentBlock }> = ({ block }) => {
    const contentState = editorState.getCurrentContent();
    const key = block.getEntityAt(0);
    if (!key) return null;
    const entity = contentState.getEntity(key);
    const url = entity.getData().url;
    const id = extractTweetId(url);
    if (!id) return null;
    return (
      <div style={{ margin: "0.5rem 0" }}>
        <TwitterEmbed id={id} />
      </div>
    );
  };
  const AtomicExternal: FC<{ block: ContentBlock }> = ({ block }) => {
    const contentState = editorState.getCurrentContent();
    const key = block.getEntityAt(0);
    if (!key) return null;
    const entity = contentState.getEntity(key);
    const url = entity.getData().url;

    const { data: oembedData } = useGetOembedQuery(
      url
        ? {
            url,
            maxwidth: 500,
            theme: "light",
          }
        : skipToken
    );

    if (!oembedData?.html) return null;

    return (
      <div style={{ margin: "0.5rem 0" }}>
        <ExternalPreview html={oembedData.html} />
      </div>
    );
  };

  const blockRendererFn = useCallback(
    (block: ContentBlock) => {
      if (block.getType() === "atomic") {
        const key = block.getEntityAt(0);
        if (!key) return null;

        const entity = editorState.getCurrentContent().getEntity(key);
        if (entity.getType() === "TWITTER") {
          return {
            component: AtomicTweet,
            editable: false,
            props: { block },
          };
        }
        if (entity.getType() === "EXTERNAL") {
          return {
            component: AtomicExternal,
            editable: false,
            props: { block },
          };
        }

        if (entity.getType() === "POST") {
          return {
            component: AtomicPost,
            editable: false,
            props: { block },
          };
        }

        if (entity.getType() === "YOUTUBE") {
          return {
            component: AtomicYouTube,
            editable: false,
            props: {},
          };
        }
      }
      return null;
    },
    [editorState]
  );

  const myKeyBindingFn = useCallback(
    (e: React.KeyboardEvent): string | null => {
      if (e.key === "Enter" && searchTerm && dropdownItems.length > 0) {
        return null;
      }
      return getDefaultKeyBinding(e);
    },
    [searchTerm, dropdownItems.length]
  );

  return (
    <Stack direction="row" gap="0.75rem">
      <div
        ref={editorWrapperRef}
        style={{
          // overflow: "auto",
          // maxHeight: "119px",
          width: "100%",
          fontFamily: "Inter",
          fontWeight: 500,
          fontStyle: "normal",
          fontSize: "15px",
          lineHeight: "24px",
          letterSpacing: "0%",
          position: "relative",
          minHeight: postVariant === "Economics" ? "48px" : "24px",
        }}
        onKeyDown={handleKeyDown}
      >
        <Editor
          ref={editorRef}
          editorState={editorState}
          onChange={onChange}
          placeholder={placeholder}
          handleBeforeInput={handleBeforeInput}
          handlePastedText={handlePastedText}
          blockRendererFn={blockRendererFn}
          keyBindingFn={myKeyBindingFn}
          spellCheck={true}
        />
        {searchTerm && (
          <MentionDropdown
            items={dropdownItems}
            highlightedIndex={highlightedIndex}
            onSelect={insertMention}
            position={dropdownPos}
          />
        )}
      </div>
    </Stack>
  );
};

export default PostEditor;
